import { ContentType } from '@/@types/common'
import type { Notification } from '@/@types/notification'
import { useStoryViewer } from '@/components/Story/viewer/StoryViewerContext'
import { Register, type NavigateOptions } from '@tanstack/react-router'
import type { MouseEvent } from 'react'
import { fetchOriginalPost, fetchRepost, getUniversalPostFetcher } from '../fetchers/post'
import { getUserProfile } from '../fetchers/user'
import parseJikeURLScheme, { type JikeURLScheme } from '../jikeURLScheme'
import { POST_TYPE_TO_ROUTE_MAPPING } from '../route'
import useUniversalNavigate from './useUniversalNavigate'

async function getNavigationOptions(
	scheme: JikeURLScheme
): Promise<NavigateOptions<Register['router']>> {
	switch (scheme.type) {
		case 'USER': {
			const user = await getUserProfile(scheme.username)
			if (!user?.username) throw new Error('No valid user')

			return { to: '/u/$username', params: { username: user.username } }
		}
		case 'ORIGINAL_POST': {
			const post = await fetchOriginalPost(scheme.id)
			const username = post.user?.username
			const type = post.type && POST_TYPE_TO_ROUTE_MAPPING[post.type]

			if (!post || !username || !type) throw new Error('No valid post')

			return {
				to: scheme.commentId ? '/u/$username/$type/$id/$commentId' : '/u/$username/$type/$id',
				params: { username, id: post.id, commentId: scheme.commentId, type },
			}
		}
		case 'REPOST': {
			const post = await fetchRepost(scheme.id)
			const username = post.user?.username
			const type = post.type && POST_TYPE_TO_ROUTE_MAPPING[post.type]

			if (!post || !username || !type) throw new Error('No valid repost')

			return { to: '/u/$username/$type/$id', params: { username, id: post.id, type } }
		}
		case 'COMMENT': {
			const post = await getUniversalPostFetcher(scheme.targetType)?.(scheme.targetId)
			const username = post?.user?.username
			const type = post?.type && POST_TYPE_TO_ROUTE_MAPPING[post.type]

			if (!post || !username || !type) throw new Error('No valid post or comment')

			return {
				to: '/u/$username/$type/$id/$commentId',
				params: { username, id: post.id, commentId: scheme.id, type },
			}
		}
		default:
			throw new Error('Unsupported Scheme Type')
	}
}

export default function useNotificationClickHandler(notification: Notification) {
	const navigate = useUniversalNavigate()
	const { openStoryViewer } = useStoryViewer()

	const handleClick = async (event: MouseEvent) => {
		try {
			// React events from portals still bubble through the component tree. Ignore those
			// events because the portal (for example, an image lightbox) is not part of the
			// notification card's visible interaction area.
			if (!(event.target instanceof Node) || !event.currentTarget.contains(event.target)) return

			if (!notification.linkUrl) throw new Error('No valid linkUrl')

			const scheme = parseJikeURLScheme(notification.linkUrl)
			if (scheme.type === 'STORY') {
				openStoryViewer({
					type: 'STORY',
					storyId: scheme.id,
				})
				return
			}
			if (scheme.type === 'COMMENT' && scheme.targetType === ContentType.STORY) {
				openStoryViewer({
					type: 'STORY',
					storyId: scheme.targetId,
				})
				return
			}

			navigate(await getNavigationOptions(scheme), event)
		} catch {
			return
		}
	}

	return { handleClick, isClickable: Boolean(notification.linkUrl) }
}
